import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useFeatures } from "../lib/features";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";
import {
  LayoutDashboard, Sparkles, Clock, Settings, LogOut, User,
  FileCheck, ChevronLeft, ChevronRight, FolderOpen, FileText, Briefcase, CreditCard, Shield,
  Sun, Moon, BarChart3, Mail, Users, Trophy, Building2, Bell, ShieldCheck, Lock
} from "lucide-react";
import { useState } from "react";
import EmailVerifyBanner from "./EmailVerifyBanner";
import UserSearch from "./UserSearch";
import NotificationsBell from "./NotificationsBell";

const LOCK_MESSAGE = "Temporarily unavailable, please try after some time";

const NAV_ITEMS = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, lockKey: "dashboard_locked" },
  { to: "/app/optimize", icon: Sparkles, label: "Optimize", lockKey: "optimize_locked" },
  { to: "/app/match-score", icon: FileCheck, label: "Match Score", lockKey: "match_score_locked" },
  { to: "/app/cover-letter", icon: FileText, label: "Cover Letter", lockKey: "cover_letter_locked" },
  { to: "/app/write-email", icon: Mail, label: "Application Email", lockKey: "application_email_locked" },
  { to: "/app/resumes", icon: FolderOpen, label: "Resumes", lockKey: "resumes_locked" },
  { to: "/app/history", icon: Clock, label: "History", lockKey: "history_locked" },
  { to: "/app/tracker", icon: Briefcase, label: "Job Tracker", lockKey: "job_tracker_locked" },
  { to: "/app/squads", icon: Users, label: "Squads", flag: "squads_enabled", lockKey: "squads_locked" },
  { to: "/app/agencies", icon: Building2, label: "Agencies", flag: "agencies_enabled", lockKey: "agencies_locked" },
  { to: "/app/leaderboard", icon: Trophy, label: "Leaderboard", flag: "leaderboard_visible", lockKey: "leaderboard_locked" },
  { to: "/app/pricing", icon: CreditCard, label: "Pricing", flag: "pricing_enabled", lockKey: "pricing_locked" },
  { to: "/app/analytics", icon: BarChart3, label: "Analytics", lockKey: "analytics_locked" },
  { to: "/app/profile", icon: User, label: "Profile", lockKey: "profile_locked" },
  { to: "/app/privacy", icon: ShieldCheck, label: "Privacy", lockKey: "privacy_locked" },
  { to: "/app/settings", icon: Settings, label: "Settings", lockKey: "settings_locked" },
];

function agencyNavItems(primaryId) {
  return [
    { to: primaryId ? `/app/agencies/${primaryId}` : "/app/agencies", icon: Building2, label: "Agency Dashboard", end: true, flag: "agencies_enabled", lockKey: "agencies_locked" },
    { to: "/app/notifications", icon: Bell, label: "Notifications" },
    { to: "/app/settings", icon: Settings, label: "Settings", lockKey: "settings_locked" },
  ];
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { features } = useFeatures();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = (user?.workspace_mode === "agency")
    ? agencyNavItems(user?.primary_agency_id)
    : NAV_ITEMS;

  const isLockedItem = (item) =>
    user?.role !== "admin" &&
    (
      (item.flag && features[item.flag] === false) ||
      (item.lockKey && features[item.lockKey] === true)
    );

  // A tab is HIDDEN (removed from the sidebar) when its derived *_hidden flag is true.
  // Admins always see everything; Dashboard is never hideable.
  const isHiddenItem = (item) =>
    user?.role !== "admin" &&
    item.lockKey &&
    item.lockKey !== "dashboard_locked" &&
    features[item.lockKey.replace("_locked", "_hidden")] === true;

  const visibleNavItems = navItems.filter((item) => !isHiddenItem(item));

  const plan = user?.subscription?.plan || "trial";
  const isUnlimited = plan === "unlimited";
  const u = user?.usage || {};
  const optLimit = u.opt_limit ?? u.scans_limit ?? 0;
  const optUsed = u.opt_used ?? u.scans_used ?? 0;
  const optAddon = u.opt_addon ?? u.addon_scans ?? 0;
  const scanLimit = u.scan_limit ?? u.scans_limit ?? 0;
  const scanUsed = u.scan_used ?? 0;
  const scanAddon = u.scan_addon ?? 0;
  const optRemaining = isUnlimited ? null : Math.max(0, optLimit - optUsed + optAddon);
  const scanRemaining = isUnlimited ? null : Math.max(0, scanLimit - scanUsed + scanAddon);
  const optPct = isUnlimited ? 0 : (optLimit > 0 ? Math.min(100, Math.round((optUsed / optLimit) * 100)) : 0);
  const scanPct = isUnlimited ? 0 : (scanLimit > 0 ? Math.min(100, Math.round((scanUsed / scanLimit) * 100)) : 0);
  const planLabel = plan === "trial" ? "Free Trial" : plan === "base" ? "Base" : plan === "unlimited" ? "Unlimited" : plan;

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <div className={`dash-layout ${collapsed ? "dash-collapsed" : ""}`} data-testid="dashboard-layout">
      <aside className="dash-sidebar" data-testid="dashboard-sidebar">
        <div className="dash-sidebar-top">
          <div className="dash-sidebar-brand" onClick={() => navigate("/app")} data-testid="sidebar-brand">
            <div className="dash-brand-icon"><FileCheck size={16} /></div>
            {!collapsed && <span className="dash-brand-text">ResumeAI</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
            {!collapsed && (
              <button
                className="dash-theme-toggle"
                onClick={toggleTheme}
                data-testid="theme-toggle-dashboard"
                title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              >
                {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
              </button>
            )}
            <button
              className="dash-collapse-btn"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="sidebar-collapse-btn"
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
        </div>

        <nav className="dash-sidebar-nav">
          {visibleNavItems.map((item) => {
            const { to, icon: Icon, label, end } = item;
            const testid = `nav-${label.toLowerCase().replace(" ", "-")}`;
            if (isLockedItem(item)) {
              return (
                <button
                  key={to}
                  type="button"
                  className="dash-nav-item"
                  data-testid={`${testid}-locked`}
                  onClick={() => toast.error(LOCK_MESSAGE)}
                  style={{ width: "100%", background: "none", border: "none", cursor: "pointer", opacity: 0.55, font: "inherit", textAlign: "left" }}
                >
                  <Icon size={18} />
                  {!collapsed && (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {label} <Lock size={12} />
                    </span>
                  )}
                </button>
              );
            }
            return (
              <NavLink
                key={to} to={to} end={end}
                className={({ isActive }) => `dash-nav-item ${isActive ? "active" : ""}`}
                data-testid={testid}
              >
                <Icon size={18} />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="dash-sidebar-usage" data-testid="sidebar-usage">
            <div className="dash-usage-header">
              <span className="dash-usage-label">Optimizations</span>
              <span className="dash-usage-count" data-testid="sidebar-opt-remaining">
                {isUnlimited ? "Unlimited" : `${optRemaining} left`}
              </span>
            </div>
            {!isUnlimited && (
              <div className="dash-usage-bar">
                <div className="dash-usage-fill" style={{ width: `${100 - optPct}%` }} />
              </div>
            )}
            <div className="dash-usage-header" style={{ marginTop: "0.6rem" }}>
              <span className="dash-usage-label">Scans</span>
              <span className="dash-usage-count" data-testid="sidebar-scans-remaining">
                {isUnlimited ? "Unlimited" : `${scanRemaining} left`}
              </span>
            </div>
            {!isUnlimited && (
              <div className="dash-usage-bar">
                <div className="dash-usage-fill" style={{ width: `${100 - scanPct}%` }} />
              </div>
            )}
            <div className="dash-usage-plan">
              <span className="dash-plan-badge" data-testid="sidebar-plan-badge">{planLabel}</span>
            </div>
          </div>
        )}

        <div className="dash-sidebar-bottom">
          {user?.role === "admin" && (
            <button className="dash-nav-item admin-link" onClick={() => navigate("/admin")} data-testid="sidebar-admin-link">
              <Shield size={18} />
              {!collapsed && <span>Admin Panel</span>}
            </button>
          )}
          <div className="dash-sidebar-user" data-testid="sidebar-user">
            <div className="dash-user-avatar"><User size={14} /></div>
            {!collapsed && (
              <div className="dash-user-details">
                <span className="dash-user-name">{user?.name || "User"}</span>
                <span className="dash-user-email">{user?.email || ""}</span>
              </div>
            )}
          </div>
          <button className="dash-logout-btn" onClick={handleLogout} data-testid="sidebar-logout-btn" title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="dash-main" data-testid="dashboard-main">
        <EmailVerifyBanner />
        <div className="dash-topbar" data-testid="dash-topbar" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.8rem", padding: "0.85rem 1.5rem 0" }}>
          <UserSearch />
          <NotificationsBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
